import { InclusionCode } from "@silversea-ssc/web-inclusions";
import { FareCode } from "@silversea-ssc/web-fares";
import {
  FareInfoV2,
  InclusionInfo
} from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { InfographicConfig } from "@silversea-ssc/ui-components-react";

export type DayByDay = {
  inclusionCode: InclusionCode;
  date: string;
  fares: FareCode[];
};

export type CruiseDayByDay = {
  pre: DayByDay[];
  post: DayByDay[];
};

const preCruiseGroupId = "pre-cruise";
const postCruiseGroupId = "post-cruise";

export const cruiseDayByDay = (
  fares: readonly FareInfoV2[],
  infographicConfig: InfographicConfig,
  departureDate: string | undefined
): CruiseDayByDay => {
  const inclusionItemsWithFare: DayByDay[] = [...fares]
    .sort((a, b) => b.inclusionItems.length - a.inclusionItems.length)
    .flatMap((x) => x.inclusionItems)
    .reduce(distinctByInclusionInfo, [])
    .map((x) => ({
      inclusionCode: x.code,
      date: x.date,
      fares: findFareByInclusion(x, fares)
    }));

  const pre = dayByDaysFor(
    preCruiseGroupId,
    infographicConfig,
    inclusionItemsWithFare,
    isPreCruise(departureDate)
  );

  const post = dayByDaysFor(
    postCruiseGroupId,
    infographicConfig,
    inclusionItemsWithFare,
    isPostCruise(departureDate)
  );

  return { pre, post };
};

const inclusionsFromInfographic = (
  groupId: string,
  infographicConfig: InfographicConfig
) =>
  infographicConfig.groups
    .find((x) => x.id === groupId)
    ?.steps.flatMap((step) => step.inclusions) || [];

const dayByDaysFor = (
  groupId: string,
  infographicConfig: InfographicConfig,
  dayByDays: DayByDay[],
  isPreOrPost: (x: DayByDay) => boolean
) => {
  const groupInclusions = inclusionsFromInfographic(groupId, infographicConfig);
  return dayByDays.filter(
    (x) => groupInclusions.includes(x.inclusionCode) && isPreOrPost(x)
  );
};

const findFareByInclusion = (
  inclusionInfo: InclusionInfo,
  fares: readonly FareInfoV2[]
) => {
  const includedFares = fares.filter(({ inclusionItems }) =>
    inclusionItems.some(
      (item) =>
        item.code === inclusionInfo.code && item.date === inclusionInfo.date
    )
  );

  return Array.from(new Set(includedFares.map((x) => x.fare)));
};

const distinctByInclusionInfo = (
  acc: InclusionInfo[],
  curr: InclusionInfo
): InclusionInfo[] => {
  const alreadyPresent = acc.find(
    (x) => x.code === curr.code && x.date === curr.date
  );
  return alreadyPresent ? acc : [...acc, curr];
};

const isPreCruise = (departureDate: string | undefined) => (x: DayByDay) =>
  departureDate ? x.date <= departureDate : true;
const isPostCruise = (departureDate: string | undefined) => (x: DayByDay) =>
  departureDate ? x.date > departureDate : true;
